import { defineComponent, nextTick, reactive, toRefs, watch, onMounted, computed, isReactive, } from 'vue';
import { withInstall } from '../_util/type';
import { QuotationColor } from '../_util/enum';
import { TreeNodeType, ComponentType, NodeLineType } from './utils/types';
import { APPEND_EMPTY_X_GAP, INSERT_JOIN_X_GAP, INSERT_JOIN_X_GAP_MERGE, ICON_SIZE, SHRINK_CLOSE_X_GAP, SHRINK_OPEN_X_GAP, SHRINK_OPEN_X_GAP_MERGE, APPEND_X_GAP, } from './utils/const';
import useConfigInject from '../_util/hooks/useConfigInject';
import ScrollBar from '../scrollbar';
import classNames from '../_util/classNames';
import * as d3 from 'd3';
import Graph from './utils/graph';
import { renderStgyGraph, appendNode, addModuleBrotherNode, insertNode, addModuleChildNode, mergeNode, cancelMerge, editMerge, initShrinkNode, shrinkOrExpandNode, findLinkChildById, deleteSingleNodeRelation, deleteMergeNodeRelation, appendDragNode, deleteDragNodeAndLink, disabledNodeDrag, findNodeByCoordinate, updateDragNode, lightenPath, getCurrentFatherAdnSonNode, getCurrentFatherNode, getCurrentChildrenNode, getCurrentBrother, getCompletionPreRelationList, } from './utils/tools';
import raf from '../_util/raf';
import { useCreateVueApp } from './utils/useApp';
// import type { NodeProps } from './Node';
export const stgyGraphProps = () => ({
    relationMark: {
        type: String,
        default: 'id',
    },
    startMark: {
        type: String,
        default: 'startId',
    },
    endMark: {
        type: String,
        default: 'endId',
    },
    draggable: {
        type: Boolean,
        default: false,
    },
    miniStartNode: {
        type: Boolean,
        default: false,
    },
    origin: {
        type: Array,
        default: () => [0, 0],
    },
    nodeList: {
        type: Array,
        default: () => [],
    },
    linkList: {
        type: Array,
        default: () => [],
    },
    menus: {
        type: Array,
        default: () => [
            {
                name: '条件',
                value: TreeNodeType.CONDITION,
                disabled: false,
            },
            {
                name: '工具',
                value: TreeNodeType.TOOLS,
                disabled: false,
            },
        ],
    },
    // 选中的node id 集合
    selectedNodeId: {
        type: Array,
        default: () => [],
    },
    // 选中的link的activeId
    selectedActiveId: {
        type: Number,
        default: -1,
    },
    linkPadding: {
        type: Number,
        default: 50,
    },
    merge: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    quotationColor: {
        type: Number,
        default: QuotationColor.RED_UP_GREEN_DOWN,
    },
    hiddenAppend: {
        type: Boolean,
        default: false,
    },
});
const StgyGraph = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'WdStgyGraph',
    props: stgyGraphProps(),
    emits: [
        'node-delete',
        'node-shrink',
        'menu-click',
        'node-append',
        'node-insert',
        'node-click',
        'link-click',
        'node-drag-end',
    ],
    slots: ['node-header', 'node-content'],
    setup(props, { slots, emit, expose }) {
        const { prefixCls, configProvider } = useConfigInject('stgy-graph', props);
        const { relationMark, startMark, endMark, origin, menus, readonly, draggable, selectedActiveId, selectedNodeId, miniStartNode, hiddenAppend, } = toRefs(props);
        const state = reactive({
            graphRef: null,
            scrollRef: null,
            svgContainer: null,
            renderNodeList: [],
            renderLinkList: [],
            graph: new Graph({
                relationMark: relationMark.value,
                startMark: startMark.value,
                endMark: endMark.value,
                origin: origin.value,
                readonly: readonly.value,
                hiddenAppend: hiddenAppend.value,
            }),
            // 元素拖动配置
            moveNodeConf: {
                sourceNode: null,
                startNode: null,
                targetNode: null,
                direction: null,
                disabledDrag: false,
                disabledDragging: false, // 是否禁用拖动过程
            },
            rafRef: null,
            nodeList: [],
            linkList: [],
        });
        const { graphRef, scrollRef, svgContainer, renderNodeList, renderLinkList, graph, moveNodeConf, rafRef, nodeList, linkList, } = toRefs(state);
        let relationMap = {
            parentMap: {},
            subMap: {},
            relationList: [],
        };
        const defaultLineColor = computed(() => configProvider?.mode === 'light' ? '#C7CBD6' : '#9094a9');
        const activeLineColor = computed(() => configProvider?.mode === 'light' ? '#FF9917' : '#e8a33c');
        // 拖拽颜色
        const dragColor = computed(() => (configProvider?.mode === 'light' ? '#2469F2' : '#3578fd'));
        watch(() => configProvider?.mode, () => {
            updateLinkColor(); // 更新连线颜色
            updateLineColor(); // 更新线段颜色
        });
        onMounted(() => {
            const container = d3.select(graphRef.value);
            svgContainer.value = container.append('svg').attr('width', '100%').attr('height', '100%');
        });
        // 渲染函数
        const render = ({ linkArr, nodeArr, changeNodeAttributes }) => {
            return new Promise(resolve => {
                nextTick(() => {
                    handleRenderNode();
                    relationMap = renderStgyGraph({
                        linkList: linkArr,
                        nodeList: nodeArr,
                        changeNodeAttributes,
                        renderLinkList: renderLinkList.value,
                        nodeMap: graph.value.originPointMap(nodeArr),
                        origin: origin.value,
                        miniStartNode: miniStartNode.value,
                    });
                    resolve(relationMap);
                    updateContainerRect();
                    scrollRef.value.update(); // 更新滚动条
                    createChart();
                });
            });
        };
        // 创建画布
        const createChart = () => {
            graph.value.initNode(renderNodeList.value);
            graph.value.initLink(renderLinkList.value);
            graph.value.initButtonAndLine(linkList.value); // 初始化按钮及线段
            createLink();
            createNode();
            createShrink();
            createShrinkLine();
            createInsert();
            createInsertLine();
            createAppend();
        };
        const vueAppNodeInstances = {};
        const nodePropsMap = {}; // nodeProps Map
        // 创建节点
        const createNode = () => {
            if (graph.value.nodeList?.length > 0) {
                const nodes = svgContainer.value
                    .selectAll('.node-group')
                    .data(graph.value.nodeList, d => d.id); // 使用id作为唯一key;
                const enterSelection = nodes
                    .enter()
                    .append('g')
                    .attr('class', 'node-group')
                    .each((d, nodeIndex, nodes) => {
                    const group = d3.select(nodes[nodeIndex]);
                    group
                        .append('foreignObject')
                        .attr('width', d.width)
                        .attr('height', d.height)
                        .style('border-radius', '8px')
                        .append('xhtml:div')
                        .attr('class', 'node-wrapper');
                });
                // 合并并更新数据
                const updateSelection = enterSelection.merge(nodes);
                // update merge data
                updateSelection
                    .select('foreignObject')
                    .attr('x', d => d.coordinate[0])
                    .attr('y', d => d.coordinate[1])
                    .attr('overflow', 'visible')
                    .select('.node-wrapper')
                    .each((node, nodeIndex, child) => {
                    const el = child[nodeIndex];
                    const key = node.id;
                    const renderSlots = {
                        header: () => slots?.header?.(node),
                    };
                    slots?.content &&
                        Object.assign(renderSlots, {
                            content: () => slots.content(node),
                        });
                    slots?.tools &&
                        Object.assign(renderSlots, {
                            tools: () => slots?.tools?.({ node, updateNodeData }),
                        });
                    const nodeProps = {
                        node,
                        miniStartNode: miniStartNode.value,
                        readonly: readonly.value,
                        selectedNodeId: selectedNodeId.value,
                    };
                    if (!isReactive(nodePropsMap[key])) {
                        nodePropsMap[key] = reactive(nodeProps);
                    }
                    else {
                        Object.assign(nodePropsMap[key], nodeProps);
                    }
                    useCreateVueApp({
                        key,
                        props: {
                            nodeDelete,
                            nodeShrink,
                            menuClick,
                            nodeAppend,
                            nodeInsert,
                            nodeMousedown,
                            nodeMouseup,
                        },
                        componentKey: ComponentType.GRAPH_NODE,
                        el,
                        slots: renderSlots,
                        vueAppInstances: vueAppNodeInstances,
                        extParam: nodePropsMap[key],
                    });
                });
                // 处理已删除的元素
                nodes
                    .exit()
                    .remove()
                    .each(node => {
                    const key = node.id;
                    vueAppNodeInstances[key]?.destroy();
                    nodePropsMap[key] = null;
                });
            }
        };
        // 创建【添】
        const vueAppAppendInstances = {};
        const appendPropsMap = {};
        const createAppend = () => {
            const appendNodeList = readonly.value || hiddenAppend.value ? [] : graph.value.appendNodeList;
            const appendNodes = svgContainer.value
                .selectAll('.node-append')
                .data(appendNodeList, d => d.id);
            const enterSelection = appendNodes
                .enter()
                .append('g')
                .attr('class', 'node-append')
                .each((_d, i, nodes) => {
                const group = d3.select(nodes[i]);
                group
                    .append('foreignObject')
                    .attr('width', ICON_SIZE)
                    .attr('height', ICON_SIZE)
                    .attr('overflow', 'visible')
                    .append('xhtml:div')
                    .attr('class', 'node-append-wrapper');
            });
            enterSelection
                .merge(appendNodes)
                .select('foreignObject')
                .attr('x', d => {
                // d表示node
                return d.coordinate[0] - APPEND_X_GAP;
            })
                .attr('y', d => d.coordinate[1] + 6)
                .select('.node-append-wrapper')
                .each((node, i, child) => {
                const el = child[i];
                const key = node.id;
                const appendProps = {
                    node,
                    menus: menus.value,
                };
                if (!isReactive(appendPropsMap[key])) {
                    appendPropsMap[key] = reactive(appendProps);
                }
                else {
                    Object.assign(appendPropsMap[key], appendProps);
                }
                useCreateVueApp({
                    key,
                    props: {
                        menuClick,
                        nodeAppend,
                        getMenuPopupContainer: () => graphRef.value,
                    },
                    componentKey: ComponentType.APPEND,
                    el,
                    vueAppInstances: vueAppAppendInstances,
                    extParam: appendPropsMap[key],
                });
            });
            appendNodes
                .exit()
                .remove()
                .each(node => {
                const key = node.id;
                vueAppAppendInstances[key].destroy();
                appendPropsMap[key] = null;
            });
        };
        // 创建【+】
        const vueAppInsertInstances = {};
        const insertPropsMap = {};
        const createInsert = () => {
            const insertNodeList = readonly.value ? [] : graph.value.insertNodeList;
            const appendNodes = svgContainer.value
                .selectAll('.node-insert')
                .data(insertNodeList, d => d.id);
            const enterSelection = appendNodes
                .enter()
                .append('g')
                .attr('class', 'node-insert')
                .each((_d, i, nodes) => {
                const group = d3.select(nodes[i]);
                group
                    .append('foreignObject')
                    .attr('width', ICON_SIZE)
                    .attr('height', ICON_SIZE)
                    .attr('overflow', 'visible')
                    .append('xhtml:div')
                    .attr('class', 'node-insert-wrapper');
            });
            enterSelection
                .merge(appendNodes)
                .select('foreignObject')
                .attr('x', d => {
                const { width, coordinate, meta: { extParam: { isMergeNode }, }, } = d;
                const left = coordinate[0] + width;
                const hasNextNodeAndNotEmpty = linkList.value.some(link => link.startId === d.id && link?.meta?.activeId === -1 && !link.meta?.isDragElement);
                const xGap = isMergeNode ? INSERT_JOIN_X_GAP_MERGE : INSERT_JOIN_X_GAP;
                const x = hasNextNodeAndNotEmpty ? left + xGap : left + APPEND_EMPTY_X_GAP;
                return x;
            })
                .attr('y', d => {
                const { coordinate } = d;
                const top = coordinate[1];
                const y = 6 + top;
                return y;
            })
                .select('.node-insert-wrapper')
                .each((node, i, child) => {
                const el = child[i];
                const key = node.id;
                const insertProps = {
                    node,
                    menus: menus.value,
                };
                if (!isReactive(insertPropsMap[key])) {
                    insertPropsMap[key] = reactive(insertProps);
                }
                else {
                    Object.assign(insertPropsMap[key], insertProps);
                }
                useCreateVueApp({
                    key,
                    props: {
                        menuClick,
                        nodeInsert,
                        getMenuPopupContainer: () => graphRef.value,
                    },
                    componentKey: ComponentType.INSERT,
                    el,
                    vueAppInstances: vueAppInsertInstances,
                    extParam: insertPropsMap[key],
                });
            });
            appendNodes
                .exit()
                .remove()
                .each(node => {
                const key = node.id;
                vueAppInsertInstances[key]?.destroy();
                insertPropsMap[key] = null;
            });
        };
        // 创建【展开收起】
        const vueAppShrinkInstances = {};
        const shrinkPropsMap = {};
        const createShrink = () => {
            const shrinkNodeList = graph.value.shrinkNodeList;
            const appendNodes = svgContainer.value
                .selectAll('.node-shrink')
                .data(shrinkNodeList, d => d.id);
            const enterSelection = appendNodes
                .enter()
                .append('g')
                .attr('class', 'node-shrink')
                .each((_d, i, nodes) => {
                const group = d3.select(nodes[i]);
                group
                    .append('foreignObject')
                    .attr('width', ICON_SIZE)
                    .attr('height', ICON_SIZE)
                    .attr('overflow', 'visible')
                    .append('xhtml:div')
                    .attr('class', 'node-shrink-wrapper');
            });
            enterSelection
                .merge(appendNodes)
                .select('foreignObject')
                .attr('x', d => {
                const { width, coordinate, meta: { extParam: { isOpen, isMergeNode }, }, } = d;
                const left = coordinate[0] + width;
                const openXGap = isMergeNode ? SHRINK_OPEN_X_GAP_MERGE : SHRINK_OPEN_X_GAP;
                const x = left + (isOpen ? openXGap : SHRINK_CLOSE_X_GAP);
                return x;
            })
                .attr('y', d => {
                const { coordinate } = d;
                const top = coordinate[1];
                const y = 6 + top;
                return y;
            })
                .select('.node-shrink-wrapper')
                .each((node, i, child) => {
                const el = child[i];
                const key = node.id;
                const shrinkProps = {
                    node,
                };
                if (!isReactive(shrinkPropsMap[key])) {
                    shrinkPropsMap[key] = reactive(shrinkProps);
                }
                else {
                    Object.assign(shrinkPropsMap[key], shrinkProps);
                }
                useCreateVueApp({
                    key,
                    props: {
                        nodeShrink,
                    },
                    componentKey: ComponentType.SHRINK_EXPAND,
                    el,
                    vueAppInstances: vueAppShrinkInstances,
                    extParam: shrinkPropsMap[key],
                });
            });
            appendNodes
                .exit()
                .remove()
                .each(node => {
                const key = node.id;
                vueAppShrinkInstances[key]?.destroy();
                shrinkPropsMap[key] = null;
            });
        };
        // 创建连线
        const createLink = () => {
            const links = svgContainer.value
                .selectAll('.link-group')
                .lower()
                .data(graph.value.linkList, d => d.id); // 使用id作为唯一key;
            const enterSelection = links
                .enter()
                .append('g')
                .attr('transform', 'translate(0.5,0.5)') // 处理dpr显示问题
                .attr('class', 'link-group')
                .attr('data-activeId', d => d.meta.activeId)
                .classed('link-group-merge', d => d.meta.activeId !== -1)
                .each((link, i, links) => {
                const group = d3.select(links[i]);
                link.createLink({
                    g: group,
                    style: {
                        color: relationLineColor(link),
                    },
                }, () => {
                    if (props.merge || props.readonly || link.meta?.activeId === -1)
                        return;
                    emit('link-click', link);
                });
            });
            const mergeSelection = enterSelection.merge(links);
            mergeSelection.select('.link-path-hot').attr('d', link => link.createPath());
            mergeSelection
                .select('.link-path')
                .attr('d', link => link.createPath())
                .attr('stroke', linkColor);
            mergeSelection
                .select('.link-start-point')
                .attr('d', link => link.createArc().startPath)
                .attr('fill', linkColor);
            mergeSelection
                .select('.link-end-point')
                .attr('d', link => link.createArc().endPath)
                .attr('fill', linkColor);
            mergeSelection.lower();
            // 将当前点击的合并线层级调整至最高
            if (selectedActiveId.value !== -1) {
                svgContainer.value.selectAll(`[data-activeId='${selectedActiveId.value}']`).raise();
            }
            links.exit().remove();
        };
        // 创建+的连线
        const createInsertLine = () => {
            const insertLineList = readonly.value ? [] : graph.value.insertLineList;
            const lines = svgContainer.value.selectAll('.line-insert').data(insertLineList, d => d.id); // 使用id作为唯一key;
            const enterSelection = lines
                .enter()
                .append('g')
                .lower()
                .attr('transform', 'translate(0.5,0.5)') // 处理dpr显示问题
                .attr('class', 'line-insert')
                .each((node, i, lines) => {
                const group = d3.select(lines[i]);
                node.createLine({
                    g: group,
                    style: {
                        color: defaultLineColor.value,
                    },
                    type: NodeLineType.INSERT,
                });
            });
            const mergeSelection = enterSelection.merge(lines);
            mergeSelection
                .select('.line-path')
                .attr('d', d => d.createPath({ type: NodeLineType.INSERT }))
                .attr('stroke', lineAndPointColor);
            mergeSelection
                .select('.line-start-point')
                .attr('d', d => d.createArc(NodeLineType.INSERT).startPath)
                .attr('fill', lineAndPointColor);
            mergeSelection
                .select('.line-end-point')
                .attr('d', d => d.createArc(NodeLineType.INSERT).endPath)
                .attr('fill', lineAndPointColor);
            mergeSelection.lower();
            lines.exit().remove();
        };
        // 创建展开收起的连线
        const createShrinkLine = () => {
            const shrinkLineList = graph.value.shrinkLineList;
            const lines = svgContainer.value.selectAll('.line-shrink').data(shrinkLineList, d => d.id); // 使用id作为唯一key;
            const enterSelection = lines
                .enter()
                .append('g')
                .lower()
                .attr('transform', 'translate(0.5,0.5)') // 处理dpr显示问题
                .attr('class', 'line-shrink')
                .each((node, i, lines) => {
                const group = d3.select(lines[i]);
                node.createLine({
                    g: group,
                    style: {
                        color: defaultLineColor.value,
                    },
                    type: NodeLineType.SHRINK,
                });
            });
            const mergeSelection = enterSelection.merge(lines);
            mergeSelection
                .select('.line-path')
                .attr('d', d => d.createPath({ type: NodeLineType.SHRINK }))
                .attr('stroke', lineAndPointColor);
            mergeSelection
                .select('.line-start-point')
                .attr('d', d => d.createArc(NodeLineType.SHRINK).startPath)
                .attr('fill', lineAndPointColor);
            mergeSelection
                .select('.line-end-point')
                .attr('d', d => d.createArc(NodeLineType.SHRINK).endPath)
                .attr('fill', lineAndPointColor);
            mergeSelection.lower();
            lines.exit().remove();
        };
        // 获取线段以及端点颜色
        const lineAndPointColor = (node) => {
            return node?.meta?.extParam?.isHighLight ? activeLineColor.value : defaultLineColor.value;
        };
        // 获取关系线段颜色
        const relationLineColor = (link) => {
            return link?.meta?.isDragElement ? dragColor.value : defaultLineColor.value;
        };
        // 路径颜色
        const linkColor = link => {
            if (link.meta?.isHighLight ||
                (selectedActiveId.value === link.meta?.activeId && link.meta?.activeId !== -1)) {
                return activeLineColor.value;
            }
            else {
                return relationLineColor(link);
            }
        };
        // 更新路径颜色
        const updateLinkColor = ({ path = '.link-path', start = '.link-start-point', end = '.link-end-point', lineColor = linkColor, } = {}) => {
            svgContainer.value.selectAll(path).attr('stroke', lineColor);
            svgContainer.value.selectAll(start).attr('fill', lineColor);
            svgContainer.value.selectAll(end).attr('fill', lineColor);
        };
        // 更新线段颜色
        const updateLineColor = () => {
            updateLinkColor({
                path: '.line-path',
                start: '.line-start-point',
                end: '.line-end-point',
                lineColor: lineAndPointColor,
            });
        };
        // 更新容器宽高
        const updateContainerRect = () => {
            const cacheWidth = 200;
            const cacheHeight = 150;
            const rect = renderNodeList.value?.reduce((prev, item) => {
                const [x, y] = item?.coordinate;
                const w = x + item.width || 0;
                if (w > prev.width) {
                    prev.width = w;
                }
                const h = y + item.height || 0;
                if (h > prev.height) {
                    prev.height = h;
                }
                return prev;
            }, {
                width: 0,
                height: 0,
            });
            const d = graphRef.value;
            if (d) {
                d.style.width = `${rect.width + cacheWidth}px`;
                d.style.height = `${rect.height + cacheHeight}px`;
            }
        };
        // 删除
        const nodeDelete = node => {
            emit('node-delete', node);
        };
        // 展开收起
        const nodeShrink = node => {
            emit('node-shrink', node);
        };
        // 处理需要渲染的数据 以及源数据赋值
        const handleRenderNode = () => {
            nodeList.value = props.nodeList;
            linkList.value = props.linkList;
            const obj = initShrinkNode({
                linkList: linkList.value,
                nodeList: nodeList.value,
            });
            renderNodeList.value = obj.renderNodeList;
            renderLinkList.value = obj.renderLinkList;
        };
        // 点击菜单
        const menuClick = obj => {
            emit('menu-click', obj);
        };
        //  点击【+】
        const nodeInsert = node => {
            emit('node-insert', node);
        };
        // 点击【添】
        const nodeAppend = node => {
            emit('node-append', node);
        };
        const nodeMouseup = node => {
            emit('node-click', node);
        };
        const deleteNodeLinkList = (id, params) => {
            // 子节点的id集合
            const subIdList = relationMap?.subMap?.[id];
            // 受影响的id (要被删除)
            const effectIds = [id];
            // 浅拷贝
            let newList = [...params.linkList];
            // 只有一个子节点
            if (subIdList?.length === 1) {
                // 子节点的id
                const nextId = subIdList[0];
                // 节点Map
                const nodeMap = graph.value.originPointMap(nodeList.value);
                // 节点是否属于合并节点
                const isSubMergeNode = nodeMap[nextId]?.meta?.extParam?.isMergeNode;
                if (isSubMergeNode) {
                    // 经过合并节点的全部路线
                    const pathList = relationMap.relationList?.filter(l => l?.includes(nextId) && l[0] !== nextId);
                    // 被删除的节点的 第一个兄弟节点
                    const broId = relationMap?.parentMap?.[nextId]?.filter(i => i !== id)?.[0];
                    // 路线是否小于2条
                    if (pathList?.length <= 2) {
                        // 小于两条线 需要删除合并节点 并且把删除节点后续的关系拼接到 兄弟节点上
                        effectIds.push(nextId);
                        newList = deleteMergeNodeRelation(id, {
                            linkList: newList,
                            mergeId: nextId,
                            broId,
                            delMerge: true,
                        });
                    }
                    else {
                        // 大于两条线 找到兄弟节点然后做替换 不需要删除合并节点
                        newList = deleteMergeNodeRelation(id, {
                            linkList: newList,
                            mergeId: nextId,
                            broId,
                        });
                    }
                    return {
                        newList,
                        effectIds,
                    };
                }
            }
            // 删除单个的节点方法
            newList = deleteSingleNodeRelation(id, {
                linkList: newList,
            });
            return {
                newList,
                effectIds,
            };
        };
        // 开始拖拽
        let offsetX = 0;
        let offsetY = 0;
        let offsetLeft = 0;
        let offsetTop = 0;
        let draggableEl = null;
        let dragFlag = false;
        let startClientX = null;
        let startClientY = null;
        // 定义一个阈值，用于判断是否为长按拖拽
        const dragThreshold = 3; // 单位像素
        const nodeMousedown = (evt, node) => {
            evt.preventDefault();
            dragFlag = false;
            const boxEl = evt.target.closest(`.${prefixCls.value}-node`);
            const el = boxEl.children[0];
            offsetX = evt.clientX - el.getBoundingClientRect().left;
            offsetY = evt.clientY - el.getBoundingClientRect().top;
            startClientX = evt.clientX;
            startClientY = evt.clientY;
            moveNodeConf.value.disabledDrag = disabledNodeDrag({
                sourceNode: node,
                linkList: linkList.value,
                draggable: draggable.value,
            });
            if (!moveNodeConf.value.disabledDrag) {
                draggableEl = el.cloneNode(true);
                window.requestAnimationFrame(() => {
                    draggableEl.style.position = 'absolute';
                    draggableEl.style.zIndex = '10';
                    draggableEl.style.opacity = '0';
                    draggableEl.style.width = el.offsetWidth + 'px';
                    draggableEl.style.height = el.offsetHeight + 'px';
                    draggableEl && graphRef.value?.appendChild(draggableEl);
                });
            }
            moveNodeConf.value.sourceNode = node;
            document.addEventListener('mousemove', docNodeMove);
            document.addEventListener('mouseup', doceNodeUp);
        };
        // 拖动中
        const docNodeMove = evt => {
            evt.preventDefault();
            evt.stopPropagation();
            if (moveNodeConf.value.disabledDrag)
                return;
            if (Math.abs(evt.clientX - startClientX) < dragThreshold &&
                Math.abs(evt.clientY - startClientY) < dragThreshold) {
                draggableEl.style.pointerEvents = 'none';
                return;
            }
            else {
                draggableEl.style.pointerEvents = 'auto';
            }
            dragFlag = true;
            const scrollWrap = scrollRef.value.wrapRef;
            const boxLeft = scrollWrap.getBoundingClientRect().left;
            const boxTop = scrollWrap.getBoundingClientRect().top;
            offsetLeft = evt.clientX - offsetX - boxLeft + scrollWrap.scrollLeft;
            offsetTop = evt.clientY - offsetY - boxTop + scrollWrap.scrollTop;
            window.requestAnimationFrame(() => {
                if (!draggableEl)
                    return;
                draggableEl.style.left = offsetLeft + 'px';
                draggableEl.style.top = offsetTop + 'px';
                draggableEl.style.opacity = '1';
                onDrag({
                    offset: [offsetLeft, offsetTop],
                    sourceNode: moveNodeConf.value.sourceNode,
                });
            });
        };
        // 拖动结束
        const doceNodeUp = evt => {
            evt.preventDefault();
            draggableEl && graphRef.value?.removeChild(draggableEl);
            draggableEl = null;
            dragFlag && onDragEnd();
            document.removeEventListener('mousemove', docNodeMove);
            document.removeEventListener('mouseup', doceNodeUp);
        };
        const onDrag = ({ sourceNode, offset }) => {
            raf.cancel(rafRef.value);
            rafRef.value = raf(() => {
                const { node, direction } = findNodeByCoordinate({
                    nodeList: nodeList.value,
                    linkList: linkList.value,
                    offset,
                });
                const { disabled, startNode } = appendDragNode({
                    sourceNode,
                    direction,
                    targetNode: node,
                    nodeList: nodeList.value,
                    linkList: linkList.value,
                    nodeMap: graph.value.nodeMap,
                });
                moveNodeConf.value.startNode = startNode;
                moveNodeConf.value.targetNode = node;
                moveNodeConf.value.direction = direction;
                moveNodeConf.value.disabledDragging = disabled;
                if (disabled) {
                    deleteDragNodeAndLink({
                        nodeList: nodeList.value,
                        linkList: linkList.value,
                    });
                }
                handleRenderNode();
                createChart();
            }, 5);
        };
        const onDragEnd = () => {
            raf.cancel(rafRef.value);
            rafRef.value = null;
            deleteDragNodeAndLink({
                nodeList: nodeList.value,
                linkList: linkList.value,
            });
            emit('node-drag-end');
        };
        // 更新拖动后的数据
        const updateDragData = () => {
            updateDragNode({
                sourceNode: moveNodeConf.value.sourceNode,
                targetNode: moveNodeConf.value.targetNode,
                linkList: linkList.value,
                direction: moveNodeConf.value.direction,
                disabled: moveNodeConf.value.disabledDragging,
                nodeMap: graph.value.originPointMap(nodeList.value),
                nodeList: nodeList.value,
            });
            moveNodeConf.value.sourceNode = null;
            moveNodeConf.value.targetNode = null;
        };
        // 旧组件copy来的 工具内更新数据的相关方法
        const updateNodeData = (node, newNode) => {
            Object.assign(node, newNode);
        };
        // 展开收起方法
        const shrinkOrExpand = nodeId => {
            shrinkOrExpandNode({
                nodeId,
                nodeList: nodeList.value,
            });
        };
        expose({
            render,
            appendNode,
            addModuleBrotherNode,
            insertNode,
            addModuleChildNode,
            mergeNode,
            cancelMerge,
            editMerge,
            findLinkChildById,
            updateDragData,
            deleteNodeLinkList,
            shrinkOrExpand,
            lightenPath,
            getCurrentFatherAdnSonNode,
            getCurrentFatherNode,
            getCurrentChildrenNode,
            getCurrentBrother,
            getCompletionPreRelationList,
        });
        return () => {
            return (<ScrollBar ref={scrollRef} view-class={`${prefixCls.value}-view`}>
          <div class={classNames({
                    [`${prefixCls.value}`]: true,
                    [`${prefixCls.value}-readonly`]: props.readonly,
                    [`${prefixCls.value}-gu`]: props.quotationColor === QuotationColor.GREEN_UP_RED_DOWN,
                    [`${prefixCls.value}-merge`]: props.merge,
                })} ref={graphRef}></div>
        </ScrollBar>);
        };
    },
});
export default withInstall(StgyGraph);
